import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { faFingerprint, faUsbDrive } from '@fortawesome/pro-solid-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subscription } from 'rxjs';

import { LoginReason } from 'app/core/security/login-redirect';
import { FingerprintService } from 'app/core/security/multi-factor-auth/fingerprint.service';
import { WebAuthnService } from 'app/core/security/multi-factor-auth/web-authn.service';
import { SecurityService } from 'app/core/security/security.service';
import { SseNotificationService } from 'app/core/sse-notification/sse-notification.service';
import { SystemMessage, SystemMessageService } from 'app/core/system-message/system-message-service';
import { GlobalVariablesService } from 'app/services/global-variables.service';
import { validateForm } from 'app/shared/form-elements/form-validateForm.function';
import { UserJsVm } from 'app/shared/generated/Administration/Models/Users/UserJsVm';
import { LoginVm } from 'app/shared/generated/Models/LoginVm';
import { FidoService } from 'app/shared/services/fido.service';
import { QuickUserInfoService } from 'app/shared/quick-user-info/quick-user-info.service';
import { GlobalService } from 'app/shared/services/global.service';
import { MultiFactorAuthModalComponent } from './multi-factor-auth-modal/multi-factor-auth-modal.component';

@Component({
    selector: 'pcg-login'
    , templateUrl: './login.component.html'
    , styleUrls: ['./login.component.scss']
    , changeDetection: ChangeDetectionStrategy.Eager
    , standalone: false
})
export class LoginComponent implements OnDestroy, OnInit {

    route = inject(ActivatedRoute);
    ms = inject(SystemMessageService);
    sec = inject(SecurityService);
    router = inject(Router);
    modalService = inject(NgbModal);
    webAuthn = inject(WebAuthnService);
    fingerprint = inject(FingerprintService);
    httpClient = inject(HttpClient);
    title = inject(Title);
    infoService = inject(QuickUserInfoService);
    public gs = inject(GlobalService);
    public gvs = inject(GlobalVariablesService);

	@ViewChild('myForm', { static: true }) myForm!: ElementRef;

    sseService = inject(SseNotificationService);
    
	loginForm = LoginVm.Form;
    user!: UserJsVm;

	userId!: number;

	showMfa = false;
    mfaTokenEnabled = false;
	/** Why the user was sent here, which picks the notice above the form. Null for a plain link. */
	loginReason: LoginReason | null = null;
	isUsingSsoLogin = false;
	isError = false;
    isMfaError = false;
    isResendCode = false;
    
	errorString = "";
    mfaErrorString = "";
	ssoEmail: string = "";	
	ssoCode = "";

	faFingerprint = faFingerprint;
	faUsbDrive = faUsbDrive;

    isLoading = signal<boolean>(false);

    /**
     * The QR login token already submitted for validation during this page load. Static because
     * the duplicate submissions this prevents come from separate LoginComponent instances, not
     * from one instance calling twice.
     */
    private static submittedQrToken: string | null = null;

    /**
     * The SSO login token already submitted for validation during this page load. Static for the
     * same reason as the QR guard above: the SSO token is single use, so a second LoginComponent
     * built by a navigation race would spend an already consumed token and surface a login error
     * after the first instance had actually succeeded.
     */
    private static submittedSsoToken: string | null = null;

    private qrLoginSubscription?: Subscription;
	
	ngOnInit() {
        this.title.setTitle('Login');
		const user = this.sec.getUser();

        // Session hygiene and any queued message run whether or not a session already exists,
        // so these stay ahead of the redirect below.
		document.cookie = "hasSpentSevenHalfMinutesInactive=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
		this.checkLoginReason();
		if (this.route.snapshot.queryParams.errorMessage != null) {
            this.ms.setSystemMessage(decodeURIComponent(this.route.snapshot.queryParams.errorMessage));
        }

        // Stop here when a session already exists. Falling through would re-run
        // validateQrTokenAndLogin() against a single use token this page load has
        // already consumed, which surfaces a login error to an authenticated user.
        if (user) {
            this.loginRedirect();
            return;
        }

		this.isUsingSsoLogin = this.route?.snapshot?.queryParams?.useSso === true.toString();
        if (this.route.snapshot.queryParams.ssoToken != null) { this.completeSsoLogin(); }
        this.validateQrTokenAndLogin();
	}

	switchSso() { this.isUsingSsoLogin = !this.isUsingSsoLogin; }

	onSubmit() {
		this.isError = false;
		this.errorString = "";

		if (this.loginForm.valid) {
            this.isLoading.set(true);            
			this.sec.setLocalStorage('lastAccess', new Date());
			this.httpClient.post<SystemMessage>("api/Account/Login", this.loginForm.getRawValue())
				.subscribe({
                    next: (msg: SystemMessage) => { 
                        if (msg.isSuccessful) { this.doLogin(msg); } 
                        else { 
                            this.isLoading.set(false);
                            this.isError = true;
                            this.errorString = msg.message!;
                        }
                    }
                    , error: () => {
                        this.isLoading.set(false);
                        this.isError = true;
                        this.errorString = "An error occurred";
                    }
				}
			);
		} else { validateForm(this.loginForm); }
	}

	doLogin (msg: SystemMessage) {
		const val = msg.value;
		if (!val) { return; }
		if (val.userId) { this.userId = val.userId; }
        if (
			val.fidoMfa 
			&& val.fingerprintMfa
		) { this.showMfa = true; }
		else if (val.fidoMfa) {
			// Handle fido MFA
			this.myForm?.nativeElement?.ownerDocument?.activeElement?.blur(); // Need this hack to avoid change detection error on modal open
			this.modalService.open(MultiFactorAuthModalComponent, { animation: false }); // Open the 2FA modal
			this.doTwoFactorAuth(); // Handle 2FA
		} else if (val.fingerprintMfa) {
			// Handle fingerprint MFA
			this.doFingerprintIdentification();
		} else if (val?.mfaTokenEnabled) {
            this.mfaTokenEnabled = true;
        } else {
			// Successful login
			this.loginForm.reset();
			this.sec.setSecurity(val.token, val.user);
            this.sseService.initializeSSE();
            localStorage.setItem("formulary-table-session", null!);
			this.loginRedirect(val.promptFacilityConfirmation, val.promptFacilityInModal);
		}
	}

	/**
	 * @param promptFacilityConfirmation Set by the server on an Issuance User / User Admin's first successful
	 * login of the day. Only honored when this redirect lands on the dashboard; an already authenticated
	 * visit to /login never passes it.
	 * @param promptFacilityInModal Set by the server when the System Admin "First Login Modal" setting is on, so the
	 * prompt opens in a centered modal instead of expanding the dashboard dropdown.
	*/
	loginRedirect(
		promptFacilityConfirmation: boolean = false
		, promptFacilityInModal: boolean = false
	) {
        const baseUrl = document.getElementsByTagName('base')[0].href;
        this.user = this.sec.getUser();
		if (localStorage.getItem("isNavFixed") === "true") { this.gvs.isNavFixed.set(true); }
        if (this.user?.needToResetPin) { globalThis.location.href = baseUrl + `/admin/users/user/edit/security/${this.user.id}`; }

		// This page's redirectUrl, or the one saved before the SSO round trip. Anything that is not an in-app path
		// (another site, the login page itself, a nested redirect) gives the dashboard.
		const returnUrl = this.sec.takeReturnUrl(this.route.snapshot.queryParamMap.get('redirectUrl'));

		// uploads/ and quartz are served outside the SPA, so they need a full page load.
		if (/^\/(?:uploads\/|quartz)/.test(returnUrl)) {
			globalThis.location.href = baseUrl + returnUrl.slice(1);
			return;
		}

		// A deep link keeps the user where they asked to go, without the daily facility prompt
		if (
            promptFacilityConfirmation
            && LoginComponent.isDashboardUrl(returnUrl)
        ) { this.infoService.queueDailyFacilityPrompt(this.user?.id, promptFacilityInModal); }

		// Replace the login page in history so Back from where they land doesn't return to it. A page they can't
		// open shows Access Denied, from its guard.
		this.router.navigateByUrl(returnUrl, { replaceUrl: true });
	}

	/** Exact dashboard route only, matching what the dashboard's own facility label treats as the dashboard */
	static isDashboardUrl(url: string | null | undefined): boolean {
		return (url ?? '').replace(/^\//, '') === 'dashboard';
	}

	doTwoFactorAuth() {
		if (!this.webAuthn.isFidoCompatible()) {
            this.isLoading.set(false);
			this.modalService.dismissAll();
			return;
		}

		// Save user credentials and clear form
		const userName = this.loginForm.value.userName;
		const password = this.loginForm.value.password;
		this.loginForm.reset();

		// Get assertion options from the server for user
		this.webAuthn.getAssertionOptions(userName, password)
            .pipe(finalize(() => this.isLoading.set(false)))
			.subscribe(async makeAssertionOptions => {
				try { 
					makeAssertionOptions = FidoService.fixAssertionOptions(makeAssertionOptions);
					let credential = await navigator.credentials.get({ publicKey: makeAssertionOptions });

					this.sec.setLocalStorage('lastAccess', new Date());
					this.webAuthn.verifyAssertion(credential, userName, password)
						.subscribe((msg: SystemMessage) => {
							this.modalService.dismissAll();
							this.doLogin(msg);
						}
					);
				} catch (e) {
					this.modalService.dismissAll();
					this.webAuthn.verifyAssertion(null, userName, password)
						.subscribe((msg: SystemMessage) => { this.doLogin(msg); });
					console.log("Error : " + e);
				} 				
			}
		);
	}

	fidoMfa() {
		// Handle fido MFA
		this.myForm?.nativeElement?.ownerDocument?.activeElement?.blur(); // Need this hack to avoid change detection error on modal open
		this.modalService.open(MultiFactorAuthModalComponent, { animation: false }); // Open the 2FA modal
		this.doTwoFactorAuth(); // Handle 2FA
	}

	fingerprintMfa() { this.doFingerprintIdentification(); }

	async doFingerprintIdentification() {
		this.isError = false;
		this.errorString = "";
		const response = await this.fingerprint.loginFingerprintMfa(this.userId);
        this.isLoading.set(false);
		// Fire the save event
		if (response !== null) {
			if (response?.isSuccessful) { 
				this.sec.setLocalStorage('lastAccess', new Date());
				this.httpClient.post<SystemMessage>("api/Account/Login?fingerprintSuccess=true", this.loginForm.getRawValue())
					.subscribe((msg: SystemMessage) => { this.doLogin(msg); });
			} else { 
				this.isError = true;
				this.errorString = "Incorrect Fingerprint.";
			}
		} 
	}

	/** Reads the reason a redirect here carried (see LoginReason). A plain link to the login page has none. */
	checkLoginReason() {
		const reason = this.route.snapshot.queryParamMap.get('reason');
		this.loginReason = reason === 'inactive' || reason === 'expired' ? reason : null;
	}

	ssoLogin() {
		this.isError = false;
		this.errorString = "";
        this.isLoading.set(true);

		this.httpClient.get<SystemMessage>(`api/Account/SsoLogin?email=${encodeURIComponent(this.ssoEmail)}`)
			.subscribe((sm: SystemMessage) => {
				if (sm.isSuccessful) {
					// The identity provider sends the user back to /login without this query string.
					this.sec.rememberReturnUrl(this.route.snapshot.queryParamMap.get('redirectUrl'));
					globalThis.location.href = sm.value;
				} else {
					this.isError = true;
					this.errorString = sm.message!;
				}
                this.isLoading.set(false);
			}
		);
	}

    /**
     * Completes an SSO login by spending the single use token the SAML redirect delivered.
     *
     * This replaces a call that passed the user id straight from the query string, which let anyone
     * log in as any user by visiting /login?userIdFromSso=<id>. The token carries no identity the
     * client can choose: the server resolves the user from its own record of the validated
     * assertion.
     */
	completeSsoLogin() {
		this.isError = false;
		this.errorString = "";
        const ssoToken = this.route.snapshot.queryParams.ssoToken;

        if (!ssoToken) { return; }

        // The token is single use, so submit a given one only once per page load. Without this a
        // navigation race that builds a second LoginComponent would spend an already consumed
        // token and show a login error to a user who is in fact being logged in.
        if (LoginComponent.submittedSsoToken === ssoToken) { return; }
        LoginComponent.submittedSsoToken = ssoToken;

        this.httpClient.get<SystemMessage>(`api/SsoLogin/ValidateToken?token=${encodeURIComponent(ssoToken)}`)
			.subscribe((msg: SystemMessage) => {
				if (msg.isSuccessful) {
					this.loginForm.reset();
					this.sec.setSecurity(msg.value.token, msg.value.user);
					localStorage.setItem("loggedInUsingSso", "true");
                    localStorage.setItem("entraIdentifierUrl", msg.value.entraIdentifierUrl);
                    localStorage.setItem("entraLoginUrl", msg.value.entraLoginUrl);
					this.loginRedirect(msg.value.promptFacilityConfirmation, msg.value.promptFacilityInModal);
				} else {
					this.isError = true;
					this.errorString = msg.message!;
				}
			}
		);
    }

    validateQrTokenAndLogin() {
		this.isError = false;
		this.errorString = "";
        // Extract the redirectUrl from queryParams
        const redirectUrl = this.route.snapshot.queryParams.redirectUrl;

        if (redirectUrl) {
            // Decode the redirectUrl
            const decodedRedirectUrl = decodeURIComponent(redirectUrl);

            // Use URLSearchParams to parse the nested query string
            const params = new URLSearchParams(decodedRedirectUrl.split('?')[1]);
            const qrToken = params.get('token'); // Extract the token

            if(!qrToken) { return; }

            // A navigation race can build more than one LoginComponent for a single scan.
            // Each would submit the same single use token, and every attempt after the first
            // would fail. Submit a given token only once per page load.
            if (LoginComponent.submittedQrToken === qrToken) { return; }
            LoginComponent.submittedQrToken = qrToken;

            this.qrLoginSubscription = this.httpClient.get<SystemMessage>(`api/QrLogin/ValidateToken?token=${qrToken}`)
                .subscribe((msg: SystemMessage) => {
                    if (msg.isSuccessful) {
                        this.loginForm.reset();
                        this.sec.setSecurity(msg.value.token, msg.value.user);
                        setTimeout(() => { this.loginRedirect(msg.value.promptFacilityConfirmation, msg.value.promptFacilityInModal); }, 300);
                    } else {
                        this.isError = true;
                        this.errorString = msg.message!;
                    }
                }
            );
        }
    }

	ngOnDestroy() {
        // A torn down instance must not keep mutating auth state or trigger its own redirect,
        // which would race the instance that replaced it.
        this.qrLoginSubscription?.unsubscribe();
    }

    verifyMfaToken(token: string) {
        this.isMfaError = false;
        this.mfaErrorString = "";

        this.httpClient.post<SystemMessage>(`api/Account/VerifyMfaToken?userId=${this.userId}&mfaCode=${token}`, null)
            .subscribe({
                next: (msg: SystemMessage) => { 
                    if (msg.isSuccessful) { 
                        this.sec.setSecurity(msg.value.token, msg.value.user);
                        localStorage.setItem("formulary-table-session", null!);
                        this.mfaTokenEnabled = false;
                        this.loginRedirect(msg.value.promptFacilityConfirmation, msg.value.promptFacilityInModal);
                    } else { 
                        this.isLoading.set(false);
                        this.isMfaError = true;
                        this.mfaErrorString = msg.message!;
                    }
                }
                , error: () => {
                    this.isLoading.set(false);
                    this.isMfaError = true;
                    this.mfaErrorString = "An error occurred";
                }
            }
        );
    }

    resendCode() {
        this.isResendCode = false;
        this.isMfaError = false;
        this.mfaErrorString = "";

        this.httpClient.post<SystemMessage>(`api/Account/ResendMfaToken?userId=${this.userId}`, null)
            .subscribe({
                next: (msg: SystemMessage) => { 
                    if (msg.isSuccessful) { this.isResendCode = true; } 
                    else { 
                        this.isMfaError = true;
                        this.mfaErrorString = msg.message!;
                    }
                }
                , error: () => {
                    this.isLoading.set(false);
                    this.isMfaError = true;
                    this.mfaErrorString = "An error occurred";
                }
            }
        );
    }
}
